import type { SelectableCardOption } from './components/common/SelectableCard';
import type { FormalwearFormValues, FormStep } from './formTypes';

export const INITIAL_FORM_VALUES: FormalwearFormValues = {
  people: {},
  customBudget: null,
  rentalBudget: null,
  eventDateStatus: null,
  comparePreference: null,
  shopPreferences: [],
  extraRequest: '',
  knownShop: {
    hasKnownShop: null,
    shopNames: [],
  },
  visitDate: null,
};

export const PEOPLE_ITEMS = [
  { key: 'groom', label: '신랑', icon: 'ri-user-smile-line' },
  { key: 'bride', label: '신부', icon: 'ri-user-heart-line' },
  { key: 'mother', label: '어머님', icon: 'ri-user-3-line' },
  { key: 'father', label: '아버님', icon: 'ri-user-3-line' },
  { key: 'femaleFamily', label: '여자(혼주, 친척, 기타)', icon: 'ri-group-line' },
  { key: 'maleFamily', label: '남자(혼주, 친척, 기타)', icon: 'ri-group-line' },
];

export const CUSTOM_BUDGET_OPTIONS: SelectableCardOption[] = [
  {
    key: 'custom-practical',
    title: '실속있게 준비할래요',
    desc: '평균 80~100만원',
    icon: 'ri-wallet-3-line',
  },
  {
    key: 'custom-standard',
    title: '대중적으로 준비할래요',
    desc: '평균 100~150만원',
    icon: 'ri-price-tag-3-line',
    badge: 'BEST',
  },
  {
    key: 'custom-premium',
    title: '고급스럽게 준비할래요',
    desc: '평균 150만원 이상',
    icon: 'ri-vip-crown-line',
  },
];

export const RENTAL_BUDGET_OPTIONS: SelectableCardOption[] = [
  {
    key: 'rental-practical',
    title: '실속있게 준비할래요',
    desc: '평균 15~20만원',
    icon: 'ri-wallet-3-line',
  },
  {
    key: 'rental-standard',
    title: '대중적으로 준비할래요',
    desc: '평균 20~35만원',
    icon: 'ri-price-tag-3-line',
    badge: 'BEST',
  },
  {
    key: 'rental-premium',
    title: '고급스럽게 준비할래요',
    desc: '평균 35만원 이상',
    icon: 'ri-vip-crown-line',
  },
];

export const EVENT_OPTIONS: SelectableCardOption[] = [
  {
    key: 'has-event-date',
    title: '행사 일정이 있어요.',
    desc: '예식, 촬영, 상견례 등 입을 날짜가 정해져 있어요.',
    icon: 'ri-calendar-check-line',
  },
  {
    key: 'no-event-date',
    title: '아직 정해지지 않았어요.',
    desc: '일정은 미정이지만 예복 준비를 먼저 알아보고 있어요.',
    icon: 'ri-question-line',
  },
];

export const COMPARE_OPTIONS: SelectableCardOption[] = [
  {
    key: 'one-recommended-shop',
    title: '전문가 추천을 믿고 딱 1곳만 방문하고 싶어요.',
    desc: '추천 정확도를 높여 불필요한 비교 상담을 줄이는 방식이에요.',
    icon: 'ri-award-line',
  },
  {
    key: 'compare-several-shops',
    title: '몇 군데 비교 상담 후 결정하고 싶어요.',
    desc: '2~3곳을 비교하면서 가격, 원단, 응대를 확인하는 방식이에요.',
    icon: 'ri-scales-3-line',
  },
];

export const SHOP_PREFERENCE_OPTIONS: SelectableCardOption[] = [
  { key: 'careful-consulting', title: '상담을 꼼꼼하게 해주는 샵', icon: 'ri-chat-check-line' },
  { key: 'many-fabrics', title: '원단/디자인 종류가 다양한 샵', icon: 'ri-shirt-line' },
  { key: 'event-shop', title: '이벤트 진행중인 샵', icon: 'ri-gift-line' },
  { key: 'well-known', title: '인지도 높은 샵', icon: 'ri-star-line' },
  { key: 'cost-effective', title: '가성비 좋은 금액대의 샵', icon: 'ri-money-dollar-circle-line' },
  { key: 'high-quality', title: '예복 퀄리티가 좋은 샵', icon: 'ri-vip-diamond-line' },
];

export const SHOP_CANDIDATES = [
  '아틀리에 로망',
  '메종 드 수트',
  '테일러 브라운',
  '루미에르 테일러',
  '블랑 수트',
  '포멀 스튜디오',
];

export const QUICK_DATE_OPTIONS = [
  { key: 'next-week', label: '다음 주' },
  { key: 'within-2-weeks', label: '2주 내' },
  { key: 'within-1-month', label: '1달 내' },
  { key: 'after-1-month', label: '1달 이후' },
];

export const FORM_STEPS: FormStep[] = [
  {
    key: 'people',
    question: '누가 입나요?',
    description: '아래의 필요한 경우 모두 선택해 주세요.',
    sections: [
      {
        type: 'counter',
        key: 'people',
        items: PEOPLE_ITEMS,
        totalLabel: (total) => `총 ${total}명 기준으로 예복 상담을 준비할게요`,
      },
    ],
    canNext: (values) => Object.values(values.people).some((count) => count > 0),
  },
  {
    key: 'budget',
    question: '예복 1벌 당 예산대는 어떻게 생각하시나요?',
    description: '예정하고 있는 예복 예산대를 선택해 주세요.',
    sections: [
      {
        type: 'select',
        key: 'customBudget',
        title: '맞춤 예복 예산대',
        options: CUSTOM_BUDGET_OPTIONS,
        variant: 'icon',
      },
      {
        type: 'select',
        key: 'rentalBudget',
        title: '대여 예복 예산대',
        options: RENTAL_BUDGET_OPTIONS,
        variant: 'icon',
      },
    ],
    canNext: (values) => values.customBudget !== null || values.rentalBudget !== null,
  },
  {
    key: 'eventDate',
    question: '언제 입으실 예정인가요?',
    description: '행사 일정에 맞게 안내를 도와드릴게요.',
    sections: [
      {
        type: 'select',
        key: 'eventDateStatus',
        options: EVENT_OPTIONS,
        variant: 'icon',
      },
    ],
    canNext: (values) => values.eventDateStatus !== null,
  },
  {
    key: 'style',
    question: '어떤 스타일을 선호하시나요?',
    description: '선호하는 스타일 및 맞춤 예복샵을 추천드릴게요.',
    sections: [
      {
        type: 'select',
        key: 'comparePreference',
        title: 'Q. 업체 및 코디를 비교하고 싶으신가요?',
        options: COMPARE_OPTIONS,
        variant: 'icon',
      },
      {
        type: 'select',
        key: 'shopPreferences',
        title: 'Q. 어떤 예복샵을 방문하고 싶으신가요?',
        desc: '최대 3개까지 선택할 수 있어요.',
        options: SHOP_PREFERENCE_OPTIONS,
        multiple: true,
        max: 3,
        variant: 'icon',
      },
      {
        type: 'textarea',
        key: 'extraRequest',
        title: 'Q. 업체 추천에 필요한 정보가 있다면 남겨주세요.',
        placeholder: '텍스트를 입력하세요.',
        rows: 5,
      },
    ],
    canNext: (values) => values.comparePreference !== null && values.shopPreferences.length > 0,
  },
  {
    key: 'knownShop',
    question: '미리 생각해두신 예복샵이 있으신가요?',
    description: '최대 3곳까지 선택할 수 있어요.',
    sections: [
      {
        type: 'knownShop',
        key: 'knownShop',
        candidates: SHOP_CANDIDATES,
        max: 3,
      },
    ],
    canNext: (values) =>
      values.knownShop.hasKnownShop === false ||
      (values.knownShop.hasKnownShop === true && values.knownShop.shopNames.length > 0),
  },
  {
    key: 'visitDate',
    question: '방문 상담 희망 일정을 선택하세요.',
    description: '상담 신청을 위한 방문 일시를 선택하세요.',
    sections: [
      {
        type: 'date',
        key: 'visitDate',
        quickOptions: QUICK_DATE_OPTIONS,
        notice: {
          title: '꼭 읽어주세요!',
          icon: 'ri-time-line',
          content:
            '한 업체당 상담 소요 시간은 30분~1시간이며, 동선과 이동시간을 고려하여 스케줄을 예약해드립니다.',
        },
        undecidedOption: {
          key: 'visit-undecided',
          title: '아직 정해지지 않았어요.',
          desc: '예약이 가능한 가장 빠른 주로 예약해드리며, 문의 답변 링크에서 취소/변경이 가능합니다.',
          icon: 'ri-calendar-event-line',
        },
      },
    ],
    canNext: (values) => values.visitDate !== null && values.visitDate !== '',
  },
];
